import React from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useAttachProofOfPayment, useGetLoanApplication } from '../../services/loanService';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../utils/rolePermissions';
import { formatCurrency } from '../../utils/formatters';
import { formatDocumentDate } from '../../utils/documentDates';
import { DocumentPreviewModal, useDocumentPreview } from './DocumentPreview';

interface DisbursementsCardProps {
  applicationId?: string;
}

/**
 * Every payout on the loan: when, how much, to whom, and the bank's proof of payment. The proof
 * often arrives after the payout, so a tranche without one can have it attached here.
 */
const DisbursementsCard: React.FC<DisbursementsCardProps> = ({ applicationId }) => {
  const { data: application, isLoading, refetch } = useGetLoanApplication(applicationId || '', {
    query: { enabled: !!applicationId }
  });
  const attachProof = useAttachProofOfPayment();
  const documentPreview = useDocumentPreview(message => toast.error(message));
  const { hasPermission } = usePermissions();
  const canAttach = hasPermission(Permission.MANAGE_DISBURSEMENTS);
  const user = useSelector((state: any) => state.auth.user);
  const [attachingId, setAttachingId] = React.useState<string | null>(null);

  const handleAttach = async (disbursementId: string, file?: File) => {
    if (!applicationId || !file) return;
    setAttachingId(disbursementId);
    try {
      await attachProof.mutateAsync({
        applicationId,
        disbursementId,
        data: { file, uploadedBy: user?.name || user?.username }
      });
      toast.success('Proof of payment attached');
      await refetch();
    } catch (error) {
      console.error('Error attaching proof of payment:', error);
      toast.error('Failed to attach the proof of payment');
    } finally {
      setAttachingId(null);
    }
  };

  const tranches = application?.disbursements || [];
  if (!applicationId || isLoading || tranches.length === 0) return null;

  const missingProof = tranches.filter(t => !t.proofOfPaymentDocumentKey).length;

  return (
    <div className="card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center py-2">
        <h6 className="card-title mb-0">
          <i className="fa fa-money me-2"></i>
          Disbursements
        </h6>
        {missingProof > 0 && (
          <span className="badge bg-warning text-dark">
            {missingProof} without proof of payment
          </span>
        )}
      </div>
      <div className="list-group list-group-flush">
        {tranches.map((tranche, index) => {
          const id = tranche.id || String(index);
          return (
            <div key={id} className="list-group-item">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                  <div className="fw-semibold">
                    {formatCurrency(tranche.disbursementAmount || 0)}
                    <span className="text-muted small fw-normal ms-2">
                      {formatDocumentDate(tranche.disbursementDate)}
                      {tranche.bankReference && ` · ref ${tranche.bankReference}`}
                    </span>
                  </div>
                  {tranche.payeeName && (
                    <div className="small text-muted">
                      Paid to {tranche.payeeName}
                      {tranche.payeeBank && `, ${tranche.payeeBank}`}
                      {tranche.payeeAccountNumber && ` ${tranche.payeeAccountNumber}`}
                      {tranche.payeeBranchCode && ` (branch ${tranche.payeeBranchCode})`}
                    </div>
                  )}
                  {tranche.outstandingConditions && (
                    <div className="small text-warning-emphasis mt-1">
                      <i className="fa fa-exclamation-triangle me-1"></i>
                      Disbursed with conditions outstanding: {tranche.outstandingConditions}
                      {tranche.conditionsOverrideReason && ` — ${tranche.conditionsOverrideReason}`}
                    </div>
                  )}
                  {tranche.notes && <div className="small text-muted mt-1">{tranche.notes}</div>}
                </div>
                <div className="text-end">
                  {tranche.proofOfPaymentDocumentKey ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success"
                      disabled={documentPreview.openingKey === tranche.proofOfPaymentDocumentKey}
                      onClick={() => documentPreview.open(tranche.proofOfPaymentDocumentKey!, 'Proof of payment')}
                    >
                      <i className="fa fa-eye me-1"></i>Proof of payment
                    </button>
                  ) : canAttach && tranche.id ? (
                    <label className={`btn btn-sm btn-outline-primary mb-0 ${attachingId === tranche.id ? 'disabled' : ''}`}>
                      <i className="fa fa-paperclip me-1"></i>
                      {attachingId === tranche.id ? 'Attaching…' : 'Attach proof of payment'}
                      <input
                        type="file"
                        className="d-none"
                        accept=".pdf,.jpg,.jpeg,.png"
                        disabled={attachingId === tranche.id}
                        onChange={(e) => {
                          handleAttach(tranche.id!, e.target.files?.[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  ) : (
                    <span className="badge bg-light text-muted border">No proof of payment yet</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <DocumentPreviewModal preview={documentPreview.preview} onClose={documentPreview.close} />
    </div>
  );
};

export default DisbursementsCard;
